import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';

import { useToaster } from '@crossnokaye/ux-common';

import { shutdownBannerRestartShutdown } from '@repo/sdk/funcs/shutdownBannerRestartShutdown';
import { ShutdownStatus } from '@repo/sdk/models/components';
import { RestartShutdownRequest } from '@repo/sdk/models/operations';

import { useWebsocketQuery } from '@/hooks/query-hooks/websockets';
import { useAuth } from '@/hooks/use-auth';
import { useGaiaClient } from '@/hooks/use-gaia-client';
import { shutdownBannerStreamQuery } from '@/queries/gaia-api/shutdown-banner-stream-query';
import { supportsShutdownQuery } from '@/queries/gaia-api/supports-shutdown-query';
import { GaiaQueryWithAgentId } from '@/queries/types';
import { setRestartPending } from '@/utils/restart-pending-cache';
import { withViewOnlyHint } from '@/utils/view-only-error-message';

/**
 * Fetches the current shutdown banner for this Agent from queryCache. This does
 * not make an HTTP request, instead it reads objects from queryCache that are populated
 * by listening to a subscription to the shutdown banner.
 *
 * @param agentId The agent ID
 * @returns The current shutdown banner for this Agent.
 */
export const useShutdownBanner = (
  agentId: string | undefined
): UseQueryResult<ShutdownStatus | null> => {
  return useWebsocketQuery<ShutdownStatus | null, ShutdownStatus | null>(
    shutdownBannerStreamQuery(agentId)
  );
};

/**
 * Restart Shutdown
 *
 * @param agentId The agent ID
 */
export const useRestartShutdown = ({
  agentId,
}: GaiaQueryWithAgentId): UseMutationResult<void, Error> => {
  const { addErrorToast, addSuccessToast } = useToaster();
  const { isViewOnly } = useAuth();
  const gaiaClient = useGaiaClient();

  return useMutation({
    mutationFn: () => {
      if (!agentId) {
        throw new Error('Agent ID is required');
      }
      return gaiaClient<RestartShutdownRequest, void>({
        gaiaFunc: shutdownBannerRestartShutdown,
        request: {
          agentId,
        },
      });
    },
    onSuccess: () => {
      if (agentId) {
        setRestartPending(agentId, true);
      }
      addSuccessToast('Building restart command has been sent');
    },
    onError: () => {
      addErrorToast(withViewOnlyHint('Failed to request restart', isViewOnly));
    },
  });
};

/**
 * Check if the agent supports shutdown
 *
 * @param agentId The agent ID
 */
export const useSupportsShutdown = ({
  agentId,
}: GaiaQueryWithAgentId): UseQueryResult<boolean | undefined> => {
  const gaiaClient = useGaiaClient();
  return useQuery(supportsShutdownQuery({ agentId, client: gaiaClient }));
};
